import { createTheme, alpha, Theme } from '@mui/material/styles';

export type ColorPaletteName =
    | 'default'
    | 'ocean'
    | 'forest'
    | 'sunset'
    | 'midnight'
    | 'sage'
    | 'coral'
    | 'gold'
    | 'arctic'
    | 'nebula'
    | 'copper'
    | 'indigo'
    | 'mocha'
    | 'matcha'
    | 'mono'
    | 'blush';

interface PaletteConfig {
    dark: Record<string, any>;
    light: Record<string, any>;
}

export const palettes: Record<ColorPaletteName, PaletteConfig> = {
    default: {
        dark: {
            primary: { main: '#c9b8ff', contrastText: '#241a4a' },
            secondary: { main: '#7fe3d4', contrastText: '#0f3d38' },
            background: { default: '#0f172a', paper: '#111827' },
            text: { primary: '#ffffff', secondary: '#e0e0e0' },
            info: { main: '#c9b8ff' },
            success: { main: '#7fe3d4' },
            warning: { main: '#ffb74d', contrastText: '#0f172a' },
            error: { main: '#ff6b6b', contrastText: '#1e1e1e' },
            snackbar: { background: '#1e293b', textColor: '#ffffff' },
            textField: { autofillBackground: '#444E5E' },
            alert: {
                success: { main: '#51cf66', contrastText: '#ffffff' },
                error: { main: '#ff6b6b', contrastText: '#ffffff' },
                warning: { main: '#ffd43b', contrastText: '#0f172a' },
                info: { main: '#6c7ee1', contrastText: '#ffffff' },
            },
        },
        light: {
            primary: { main: '#5b3fa6', contrastText: '#ffffff' },
            secondary: { main: '#1f8a7d', contrastText: '#ffffff' },
            background: { default: '#f5f3fb', paper: '#ffffff' },
            text: { primary: '#1a1a1a', secondary: '#5f6368' },
            info: { main: '#5b3fa6' },
            success: { main: '#1f8a7d' },
            warning: { main: '#ff9800', contrastText: '#ffffff' },
            error: { main: '#d32f2f', contrastText: '#ffffff' },
            snackbar: { background: '#16131f', textColor: '#ffffff' },
            textField: { autofillBackground: '#e3f2fd' },
            alert: {
                success: { main: '#2e7d32', contrastText: '#ffffff' },
                error: { main: '#c62828', contrastText: '#ffffff' },
                warning: { main: '#f57f17', contrastText: '#ffffff' },
                info: { main: '#3f51b5', contrastText: '#ffffff' },
            },
        },
    },
    ocean: {
        dark: {
            primary: { main: '#00d9ff', contrastText: '#001a33' },
            secondary: { main: '#ff6b9d', contrastText: '#3a0a1a' },
            background: { default: '#0a1428', paper: '#0d1b2a' },
            text: { primary: '#e0f7ff', secondary: '#80deea' },
            info: { main: '#00d9ff' },
            success: { main: '#26c281' },
            warning: { main: '#ffa500', contrastText: '#0a1428' },
            error: { main: '#ff4757', contrastText: '#1a1a1a' },
            snackbar: { background: '#0d3b66', textColor: '#e0f7ff' },
            textField: { autofillBackground: '#1a4d6d' },
            alert: {
                success: { main: '#26c281', contrastText: '#ffffff' },
                error: { main: '#ff4757', contrastText: '#ffffff' },
                warning: { main: '#ffa500', contrastText: '#ffffff' },
                info: { main: '#00d9ff', contrastText: '#ffffff' },
            },
        },
        light: {
            primary: { main: '#006994', contrastText: '#ffffff' },
            secondary: { main: '#d81b60', contrastText: '#ffffff' },
            background: { default: '#e0f7fa', paper: '#ffffff' },
            text: { primary: '#00838f', secondary: '#0277bd' },
            info: { main: '#0277bd' },
            success: { main: '#00796b' },
            warning: { main: '#f57c00', contrastText: '#ffffff' },
            error: { main: '#c62828', contrastText: '#ffffff' },
            snackbar: { background: '#01579b', textColor: '#ffffff' },
            textField: { autofillBackground: '#b3e5fc' },
            alert: {
                success: { main: '#00796b', contrastText: '#ffffff' },
                error: { main: '#c62828', contrastText: '#ffffff' },
                warning: { main: '#f57c00', contrastText: '#ffffff' },
                info: { main: '#0277bd', contrastText: '#ffffff' },
            },
        },
    },
    forest: {
        dark: {
            primary: { main: '#76d275', contrastText: '#1b5e20' },
            secondary: { main: '#c77dff', contrastText: '#3a0a2a' },
            background: { default: '#0e2c0d', paper: '#1b4d1a' },
            text: { primary: '#d4edda', secondary: '#a8e6a3' },
            info: { main: '#66bb6a' },
            success: { main: '#76d275' },
            warning: { main: '#ffb74d', contrastText: '#0e2c0d' },
            error: { main: '#ff6b5b', contrastText: '#1a1a1a' },
            snackbar: { background: '#1b5e20', textColor: '#d4edda' },
            textField: { autofillBackground: '#2e5d2e' },
            alert: {
                success: { main: '#4caf50', contrastText: '#ffffff' },
                error: { main: '#ff6b5b', contrastText: '#ffffff' },
                warning: { main: '#ffb74d', contrastText: '#ffffff' },
                info: { main: '#66bb6a', contrastText: '#ffffff' },
            },
        },
        light: {
            primary: { main: '#2d6a4f', contrastText: '#ffffff' },
            secondary: { main: '#9d4edd', contrastText: '#ffffff' },
            background: { default: '#f0fdf4', paper: '#ffffff' },
            text: { primary: '#1b5e20', secondary: '#2e7d32' },
            info: { main: '#2e7d32' },
            success: { main: '#558b2f' },
            warning: { main: '#f57c00', contrastText: '#ffffff' },
            error: { main: '#d32f2f', contrastText: '#ffffff' },
            snackbar: { background: '#1b5e20', textColor: '#ffffff' },
            textField: { autofillBackground: '#c8e6c9' },
            alert: {
                success: { main: '#558b2f', contrastText: '#ffffff' },
                error: { main: '#d32f2f', contrastText: '#ffffff' },
                warning: { main: '#f57c00', contrastText: '#ffffff' },
                info: { main: '#2e7d32', contrastText: '#ffffff' },
            },
        },
    },
    sunset: {
        dark: {
            primary: { main: '#ff8c42', contrastText: '#3e2723' },
            secondary: { main: '#ff6b9d', contrastText: '#3a0a1a' },
            background: { default: '#1a0e0e', paper: '#2d1810' },
            text: { primary: '#ffd1a3', secondary: '#ffb366' },
            info: { main: '#ff8c42' },
            success: { main: '#ffa07a' },
            warning: { main: '#ffc107', contrastText: '#1a0e0e' },
            error: { main: '#ff6b6b', contrastText: '#1a1a1a' },
            snackbar: { background: '#3e2723', textColor: '#ffd1a3' },
            textField: { autofillBackground: '#5d4037' },
            alert: {
                success: { main: '#ffa07a', contrastText: '#ffffff' },
                error: { main: '#ff6b6b', contrastText: '#ffffff' },
                warning: { main: '#ffc107', contrastText: '#000000' },
                info: { main: '#ff8c42', contrastText: '#ffffff' },
            },
        },
        light: {
            primary: { main: '#d84315', contrastText: '#ffffff' },
            secondary: { main: '#c2185b', contrastText: '#ffffff' },
            background: { default: '#fff8f0', paper: '#ffffff' },
            text: { primary: '#5d4037', secondary: '#d84315' },
            info: { main: '#d84315' },
            success: { main: '#ff7043' },
            warning: { main: '#ffa000', contrastText: '#ffffff' },
            error: { main: '#c62828', contrastText: '#ffffff' },
            snackbar: { background: '#3e2723', textColor: '#ffffff' },
            textField: { autofillBackground: '#ffe0b2' },
            alert: {
                success: { main: '#ff7043', contrastText: '#ffffff' },
                error: { main: '#c62828', contrastText: '#ffffff' },
                warning: { main: '#ffa000', contrastText: '#ffffff' },
                info: { main: '#d84315', contrastText: '#ffffff' },
            },
        },
    },
    midnight: {
        dark: {
            primary: { main: '#7c3aed', contrastText: '#2d0052' },
            secondary: { main: '#00d4ff', contrastText: '#000a30' },
            background: { default: '#0a0014', paper: '#1a0033' },
            text: { primary: '#e0d5ff', secondary: '#b8a5ff' },
            info: { main: '#7c3aed' },
            success: { main: '#10b981' },
            warning: { main: '#f59e0b', contrastText: '#0a0014' },
            error: { main: '#ef4444', contrastText: '#1a1a1a' },
            snackbar: { background: '#2d0052', textColor: '#e0d5ff' },
            textField: { autofillBackground: '#4a235a' },
            alert: {
                success: { main: '#10b981', contrastText: '#ffffff' },
                error: { main: '#ef4444', contrastText: '#ffffff' },
                warning: { main: '#f59e0b', contrastText: '#ffffff' },
                info: { main: '#7c3aed', contrastText: '#ffffff' },
            },
        },
        light: {
            primary: { main: '#6d28d9', contrastText: '#ffffff' },
            secondary: { main: '#0891b2', contrastText: '#ffffff' },
            background: { default: '#f5f3ff', paper: '#ffffff' },
            text: { primary: '#3730a3', secondary: '#6d28d9' },
            info: { main: '#6d28d9' },
            success: { main: '#059669' },
            warning: { main: '#d97706', contrastText: '#ffffff' },
            error: { main: '#dc2626', contrastText: '#ffffff' },
            snackbar: { background: '#3730a3', textColor: '#ffffff' },
            textField: { autofillBackground: '#e0d5ff' },
            alert: {
                success: { main: '#059669', contrastText: '#ffffff' },
                error: { main: '#dc2626', contrastText: '#ffffff' },
                warning: { main: '#d97706', contrastText: '#ffffff' },
                info: { main: '#6d28d9', contrastText: '#ffffff' },
            },
        },
    },
    sage: {
        dark: {
            primary: { main: '#6ee7b7', contrastText: '#0f3d37' },
            secondary: { main: '#f59e0b', contrastText: '#3a2a0a' },
            background: { default: '#0d1f1f', paper: '#132626' },
            text: { primary: '#d1fae5', secondary: '#a7f3d0' },
            info: { main: '#6ee7b7' },
            success: { main: '#10b981' },
            warning: { main: '#f59e0b', contrastText: '#0d1f1f' },
            error: { main: '#ef4444', contrastText: '#1a1a1a' },
            snackbar: { background: '#0f3d37', textColor: '#d1fae5' },
            textField: { autofillBackground: '#1f4d49' },
            alert: {
                success: { main: '#10b981', contrastText: '#ffffff' },
                error: { main: '#ef4444', contrastText: '#ffffff' },
                warning: { main: '#f59e0b', contrastText: '#ffffff' },
                info: { main: '#6ee7b7', contrastText: '#ffffff' },
            },
        },
        light: {
            primary: { main: '#047857', contrastText: '#ffffff' },
            secondary: { main: '#d97706', contrastText: '#ffffff' },
            background: { default: '#f0fdf4', paper: '#ffffff' },
            text: { primary: '#065f46', secondary: '#047857' },
            info: { main: '#047857' },
            success: { main: '#059669' },
            warning: { main: '#d97706', contrastText: '#ffffff' },
            error: { main: '#dc2626', contrastText: '#ffffff' },
            snackbar: { background: '#065f46', textColor: '#ffffff' },
            textField: { autofillBackground: '#a7f3d0' },
            alert: {
                success: { main: '#059669', contrastText: '#ffffff' },
                error: { main: '#dc2626', contrastText: '#ffffff' },
                warning: { main: '#d97706', contrastText: '#ffffff' },
                info: { main: '#047857', contrastText: '#ffffff' },
            },
        },
    },
    coral: {
        dark: {
            primary: { main: '#fb7185', contrastText: '#4c0519' },
            secondary: { main: '#14b8a6', contrastText: '#0a2f2d' },
            background: { default: '#1a0505', paper: '#2d0a0a' },
            text: { primary: '#feccdd', secondary: '#faa3b8' },
            info: { main: '#fb7185' },
            success: { main: '#14b8a6' },
            warning: { main: '#f59e0b', contrastText: '#1a0505' },
            error: { main: '#f87171', contrastText: '#1a1a1a' },
            snackbar: { background: '#4c0519', textColor: '#feccdd' },
            textField: { autofillBackground: '#7c1d2d' },
            alert: {
                success: { main: '#14b8a6', contrastText: '#ffffff' },
                error: { main: '#f87171', contrastText: '#ffffff' },
                warning: { main: '#f59e0b', contrastText: '#ffffff' },
                info: { main: '#fb7185', contrastText: '#ffffff' },
            },
        },
        light: {
            primary: { main: '#e11d48', contrastText: '#ffffff' },
            secondary: { main: '#0d9488', contrastText: '#ffffff' },
            background: { default: '#ffe4e6', paper: '#ffffff' },
            text: { primary: '#80213f', secondary: '#e11d48' },
            info: { main: '#e11d48' },
            success: { main: '#0d9488' },
            warning: { main: '#d97706', contrastText: '#ffffff' },
            error: { main: '#dc2626', contrastText: '#ffffff' },
            snackbar: { background: '#80213f', textColor: '#ffffff' },
            textField: { autofillBackground: '#feccdd' },
            alert: {
                success: { main: '#0d9488', contrastText: '#ffffff' },
                error: { main: '#dc2626', contrastText: '#ffffff' },
                warning: { main: '#d97706', contrastText: '#ffffff' },
                info: { main: '#e11d48', contrastText: '#ffffff' },
            },
        },
    },
    gold: {
        dark: {
            primary: { main: '#fbbf24', contrastText: '#4a3728' },
            secondary: { main: '#ec4899', contrastText: '#3a1a2d' },
            background: { default: '#1a1410', paper: '#2d1e0d' },
            text: { primary: '#fef08a', secondary: '#fcd34d' },
            info: { main: '#fbbf24' },
            success: { main: '#84cc16' },
            warning: { main: '#f59e0b', contrastText: '#1a1410' },
            error: { main: '#f87171', contrastText: '#1a1a1a' },
            snackbar: { background: '#4a3728', textColor: '#fef08a' },
            textField: { autofillBackground: '#6b4b2f' },
            alert: {
                success: { main: '#84cc16', contrastText: '#ffffff' },
                error: { main: '#f87171', contrastText: '#ffffff' },
                warning: { main: '#f59e0b', contrastText: '#ffffff' },
                info: { main: '#fbbf24', contrastText: '#ffffff' },
            },
        },
        light: {
            primary: { main: '#ca8a04', contrastText: '#ffffff' },
            secondary: { main: '#d946ef', contrastText: '#ffffff' },
            background: { default: '#fefce8', paper: '#ffffff' },
            text: { primary: '#78350f', secondary: '#ca8a04' },
            info: { main: '#ca8a04' },
            success: { main: '#65a30d' },
            warning: { main: '#d97706', contrastText: '#ffffff' },
            error: { main: '#dc2626', contrastText: '#ffffff' },
            snackbar: { background: '#78350f', textColor: '#ffffff' },
            textField: { autofillBackground: '#fef08a' },
            alert: {
                success: { main: '#65a30d', contrastText: '#ffffff' },
                error: { main: '#dc2626', contrastText: '#ffffff' },
                warning: { main: '#d97706', contrastText: '#ffffff' },
                info: { main: '#ca8a04', contrastText: '#ffffff' },
            },
        },
    },
    arctic: {
        dark: {
            primary: { main: '#9ad1ff', contrastText: '#0b1620' },
            secondary: { main: '#ffb86b', contrastText: '#0b1620' },
            background: { default: '#0b1620', paper: '#101d2a' },
            text: { primary: '#eaf4ff', secondary: '#a9c4de' },
            info: { main: '#7fb8ff' },
            success: { main: '#6ee7a8' },
            warning: { main: '#ffc65c', contrastText: '#0b1620' },
            error: { main: '#ff7a7a', contrastText: '#0b1620' },
            snackbar: { background: '#1a2c40', textColor: '#ffffff' },
            textField: { autofillBackground: '#2b4157' },
            alert: {
                success: { main: '#6ee7a8', contrastText: '#0b1620' },
                error: { main: '#ff7a7a', contrastText: '#0b1620' },
                warning: { main: '#ffc65c', contrastText: '#0b1620' },
                info: { main: '#7fb8ff', contrastText: '#0b1620' },
            },
        },
        light: {
            primary: { main: '#1b6fb0', contrastText: '#ffffff' },
            secondary: { main: '#c2610f', contrastText: '#111111' },
            background: { default: '#eef6fc', paper: '#ffffff' },
            text: { primary: '#0f2a40', secondary: '#3d5a75' },
            info: { main: '#1b6fb0' },
            success: { main: '#1a8a5a' },
            warning: { main: '#b26a00', contrastText: '#111111' },
            error: { main: '#c62828', contrastText: '#ffffff' },
            snackbar: { background: '#0f2a40', textColor: '#ffffff' },
            textField: { autofillBackground: '#cfe6f7' },
            alert: {
                success: { main: '#1a8a5a', contrastText: '#ffffff' },
                error: { main: '#c62828', contrastText: '#ffffff' },
                warning: { main: '#b26a00', contrastText: '#111111' },
                info: { main: '#1b6fb0', contrastText: '#ffffff' },
            },
        },
    },
    nebula: {
        dark: {
            primary: { main: '#e879f9', contrastText: '#14061f' },
            secondary: { main: '#facc15', contrastText: '#14061f' },
            background: { default: '#14061f', paper: '#1d0b2e' },
            text: { primary: '#f6e6ff', secondary: '#d3a9ec' },
            info: { main: '#c4a1ff' },
            success: { main: '#86efac' },
            warning: { main: '#facc15', contrastText: '#14061f' },
            error: { main: '#ff7b8f', contrastText: '#14061f' },
            snackbar: { background: '#2e1247', textColor: '#ffffff' },
            textField: { autofillBackground: '#4a2466' },
            alert: {
                success: { main: '#86efac', contrastText: '#14061f' },
                error: { main: '#ff7b8f', contrastText: '#14061f' },
                warning: { main: '#facc15', contrastText: '#14061f' },
                info: { main: '#c4a1ff', contrastText: '#14061f' },
            },
        },
        light: {
            primary: { main: '#a21caf', contrastText: '#ffffff' },
            secondary: { main: '#a16207', contrastText: '#ffffff' },
            background: { default: '#fbf1fe', paper: '#ffffff' },
            text: { primary: '#3b0f52', secondary: '#7a3d99' },
            info: { main: '#7e22ce' },
            success: { main: '#15803d' },
            warning: { main: '#a16207', contrastText: '#ffffff' },
            error: { main: '#be123c', contrastText: '#ffffff' },
            snackbar: { background: '#3b0f52', textColor: '#ffffff' },
            textField: { autofillBackground: '#f3d5fb' },
            alert: {
                success: { main: '#15803d', contrastText: '#ffffff' },
                error: { main: '#be123c', contrastText: '#ffffff' },
                warning: { main: '#a16207', contrastText: '#ffffff' },
                info: { main: '#7e22ce', contrastText: '#ffffff' },
            },
        },
    },
    copper: {
        dark: {
            primary: { main: '#d9926a', contrastText: '#14100d' },
            secondary: { main: '#6fb3a8', contrastText: '#14100d' },
            background: { default: '#14100d', paper: '#1f1813' },
            text: { primary: '#f3e6da', secondary: '#c2a892' },
            info: { main: '#7fb5d6' },
            success: { main: '#8fd19e' },
            warning: { main: '#f0b45a', contrastText: '#14100d' },
            error: { main: '#f0776a', contrastText: '#14100d' },
            snackbar: { background: '#33251c', textColor: '#ffffff' },
            textField: { autofillBackground: '#4d3a2c' },
            alert: {
                success: { main: '#8fd19e', contrastText: '#14100d' },
                error: { main: '#f0776a', contrastText: '#14100d' },
                warning: { main: '#f0b45a', contrastText: '#14100d' },
                info: { main: '#7fb5d6', contrastText: '#14100d' },
            },
        },
        light: {
            primary: { main: '#a4501f', contrastText: '#ffffff' },
            secondary: { main: '#1f7a6e', contrastText: '#ffffff' },
            background: { default: '#faf3ec', paper: '#ffffff' },
            text: { primary: '#3a2517', secondary: '#7a5a45' },
            info: { main: '#2f6f9f' },
            success: { main: '#2e7d4f' },
            warning: { main: '#a35f00', contrastText: '#ffffff' },
            error: { main: '#b3261e', contrastText: '#ffffff' },
            snackbar: { background: '#3a2517', textColor: '#ffffff' },
            textField: { autofillBackground: '#f0dccb' },
            alert: {
                success: { main: '#2e7d4f', contrastText: '#ffffff' },
                error: { main: '#b3261e', contrastText: '#ffffff' },
                warning: { main: '#a35f00', contrastText: '#ffffff' },
                info: { main: '#2f6f9f', contrastText: '#ffffff' },
            },
        },
    },
    indigo: {
        dark: {
            primary: { main: '#818cf8', contrastText: '#0b1026' },
            secondary: { main: '#fb923c', contrastText: '#0b1026' },
            background: { default: '#0b1026', paper: '#111938' },
            text: { primary: '#e8ebff', secondary: '#a8b0e6' },
            info: { main: '#93a5ff' },
            success: { main: '#5eead4' },
            warning: { main: '#fbbf24', contrastText: '#0b1026' },
            error: { main: '#fb7185', contrastText: '#0b1026' },
            snackbar: { background: '#1c2652', textColor: '#ffffff' },
            textField: { autofillBackground: '#2f3b7a' },
            alert: {
                success: { main: '#5eead4', contrastText: '#0b1026' },
                error: { main: '#fb7185', contrastText: '#0b1026' },
                warning: { main: '#fbbf24', contrastText: '#0b1026' },
                info: { main: '#93a5ff', contrastText: '#0b1026' },
            },
        },
        light: {
            primary: { main: '#4338ca', contrastText: '#ffffff' },
            secondary: { main: '#c2410c', contrastText: '#ffffff' },
            background: { default: '#eef0ff', paper: '#ffffff' },
            text: { primary: '#151b4d', secondary: '#454d99' },
            info: { main: '#4338ca' },
            success: { main: '#0f766e' },
            warning: { main: '#b45309', contrastText: '#ffffff' },
            error: { main: '#be123c', contrastText: '#ffffff' },
            snackbar: { background: '#151b4d', textColor: '#ffffff' },
            textField: { autofillBackground: '#d5d9ff' },
            alert: {
                success: { main: '#0f766e', contrastText: '#ffffff' },
                error: { main: '#be123c', contrastText: '#ffffff' },
                warning: { main: '#b45309', contrastText: '#ffffff' },
                info: { main: '#4338ca', contrastText: '#ffffff' },
            },
        },
    },
    mocha: {
        dark: {
            primary: { main: '#c8a27a', contrastText: '#17110e' },
            secondary: { main: '#8fbc8f', contrastText: '#17110e' },
            background: { default: '#17110e', paper: '#221913' },
            text: { primary: '#f5ebe0', secondary: '#c9b29b' },
            info: { main: '#a9b8d9' },
            success: { main: '#a3d9a5' },
            warning: { main: '#e8b85c', contrastText: '#17110e' },
            error: { main: '#e88a7a', contrastText: '#17110e' },
            snackbar: { background: '#35271e', textColor: '#ffffff' },
            textField: { autofillBackground: '#4f3b2d' },
            alert: {
                success: { main: '#a3d9a5', contrastText: '#17110e' },
                error: { main: '#e88a7a', contrastText: '#17110e' },
                warning: { main: '#e8b85c', contrastText: '#17110e' },
                info: { main: '#a9b8d9', contrastText: '#17110e' },
            },
        },
        light: {
            primary: { main: '#8a5a2b', contrastText: '#ffffff' },
            secondary: { main: '#3f7a4f', contrastText: '#ffffff' },
            background: { default: '#faf6f1', paper: '#ffffff' },
            text: { primary: '#3b2a1e', secondary: '#7c6350' },
            info: { main: '#5a6b99' },
            success: { main: '#3f7a4f' },
            warning: { main: '#9a6200', contrastText: '#ffffff' },
            error: { main: '#b23b2e', contrastText: '#ffffff' },
            snackbar: { background: '#3b2a1e', textColor: '#ffffff' },
            textField: { autofillBackground: '#eadccb' },
            alert: {
                success: { main: '#3f7a4f', contrastText: '#ffffff' },
                error: { main: '#b23b2e', contrastText: '#ffffff' },
                warning: { main: '#9a6200', contrastText: '#ffffff' },
                info: { main: '#5a6b99', contrastText: '#ffffff' },
            },
        },
    },
    matcha: {
        dark: {
            primary: { main: '#a3e635', contrastText: '#10130a' },
            secondary: { main: '#f472b6', contrastText: '#10130a' },
            background: { default: '#10130a', paper: '#181c0f' },
            text: { primary: '#f0f5e0', secondary: '#b9c79a' },
            info: { main: '#93c5fd' },
            success: { main: '#86efac' },
            warning: { main: '#fde047', contrastText: '#10130a' },
            error: { main: '#ff8080', contrastText: '#10130a' },
            snackbar: { background: '#262c17', textColor: '#ffffff' },
            textField: { autofillBackground: '#3b4526' },
            alert: {
                success: { main: '#86efac', contrastText: '#10130a' },
                error: { main: '#ff8080', contrastText: '#10130a' },
                warning: { main: '#fde047', contrastText: '#10130a' },
                info: { main: '#93c5fd', contrastText: '#10130a' },
            },
        },
        light: {
            primary: { main: '#4d7c0f', contrastText: '#ffffff' },
            secondary: { main: '#be185d', contrastText: '#ffffff' },
            background: { default: '#f5f9e8', paper: '#ffffff' },
            text: { primary: '#26330f', secondary: '#5a6b32' },
            info: { main: '#3b6fb0' },
            success: { main: '#15803d' },
            warning: { main: '#a16207', contrastText: '#ffffff' },
            error: { main: '#b91c1c', contrastText: '#ffffff' },
            snackbar: { background: '#26330f', textColor: '#ffffff' },
            textField: { autofillBackground: '#e0ecbf' },
            alert: {
                success: { main: '#15803d', contrastText: '#ffffff' },
                error: { main: '#b91c1c', contrastText: '#ffffff' },
                warning: { main: '#a16207', contrastText: '#ffffff' },
                info: { main: '#3b6fb0', contrastText: '#ffffff' },
            },
        },
    },
    mono: {
        dark: {
            primary: { main: '#e4e4e7', contrastText: '#0d0d0e' },
            secondary: { main: '#38bdf8', contrastText: '#0d0d0e' },
            background: { default: '#0d0d0e', paper: '#161618' },
            text: { primary: '#f2f2f3', secondary: '#a8a8ad' },
            info: { main: '#7dd3fc' },
            success: { main: '#86efac' },
            warning: { main: '#fcd34d', contrastText: '#0d0d0e' },
            error: { main: '#fb7185', contrastText: '#0d0d0e' },
            snackbar: { background: '#26262a', textColor: '#ffffff' },
            textField: { autofillBackground: '#3d3d42' },
            alert: {
                success: { main: '#86efac', contrastText: '#0d0d0e' },
                error: { main: '#fb7185', contrastText: '#0d0d0e' },
                warning: { main: '#fcd34d', contrastText: '#0d0d0e' },
                info: { main: '#7dd3fc', contrastText: '#0d0d0e' },
            },
        },
        light: {
            primary: { main: '#27272a', contrastText: '#ffffff' },
            secondary: { main: '#0369a1', contrastText: '#ffffff' },
            background: { default: '#f4f4f5', paper: '#ffffff' },
            text: { primary: '#18181b', secondary: '#52525b' },
            info: { main: '#0369a1' },
            success: { main: '#15803d' },
            warning: { main: '#a16207', contrastText: '#ffffff' },
            error: { main: '#b91c1c', contrastText: '#ffffff' },
            snackbar: { background: '#18181b', textColor: '#ffffff' },
            textField: { autofillBackground: '#e4e4e7' },
            alert: {
                success: { main: '#15803d', contrastText: '#ffffff' },
                error: { main: '#b91c1c', contrastText: '#ffffff' },
                warning: { main: '#a16207', contrastText: '#ffffff' },
                info: { main: '#0369a1', contrastText: '#ffffff' },
            },
        },
    },
    blush: {
        dark: {
            primary: { main: '#f4b6c2', contrastText: '#1a0f14' },
            secondary: { main: '#d4a373', contrastText: '#1a0f14' },
            background: { default: '#1a0f14', paper: '#26161d' },
            text: { primary: '#fbe8ee', secondary: '#dcaebd' },
            info: { main: '#c3b1e1' },
            success: { main: '#9be3b4' },
            warning: { main: '#f2c26b', contrastText: '#1a0f14' },
            error: { main: '#ff8a8a', contrastText: '#1a0f14' },
            snackbar: { background: '#3b2029', textColor: '#ffffff' },
            textField: { autofillBackground: '#59323f' },
            alert: {
                success: { main: '#9be3b4', contrastText: '#1a0f14' },
                error: { main: '#ff8a8a', contrastText: '#1a0f14' },
                warning: { main: '#f2c26b', contrastText: '#1a0f14' },
                info: { main: '#c3b1e1', contrastText: '#1a0f14' },
            },
        },
        light: {
            primary: { main: '#b83f63', contrastText: '#ffffff' },
            secondary: { main: '#9a6a2f', contrastText: '#ffffff' },
            background: { default: '#fdf3f5', paper: '#ffffff' },
            text: { primary: '#4a1d2b', secondary: '#8a4a5e' },
            info: { main: '#7a5aa6' },
            success: { main: '#2e7d50' },
            warning: { main: '#9a6200', contrastText: '#ffffff' },
            error: { main: '#b3261e', contrastText: '#ffffff' },
            snackbar: { background: '#4a1d2b', textColor: '#ffffff' },
            textField: { autofillBackground: '#f8dbe3' },
            alert: {
                success: { main: '#2e7d50', contrastText: '#ffffff' },
                error: { main: '#b3261e', contrastText: '#ffffff' },
                warning: { main: '#9a6200', contrastText: '#ffffff' },
                info: { main: '#7a5aa6', contrastText: '#ffffff' },
            },
        },
    },
};

export const getTheme = (
    mode: 'light' | 'dark',
    colorPalette: ColorPaletteName = 'default'
): Theme => {
    const currentPalette = (palettes[colorPalette] ?? palettes.default)[mode];

    const setMetaTag = (name: string, content: string) => {
        let element = document.querySelector(`meta[name="${name}"]`);
        if (!element) {
            element = document.createElement('meta');
            element.setAttribute('name', name);
            document.head.appendChild(element);
        }
        element.setAttribute('content', content);
    };

    setMetaTag('theme-color', currentPalette.background.default);
    setMetaTag('background-color', currentPalette.background.default);

    // Panels are frosted glass over the background scene, tinted by the palette.
    const glassPaper = alpha(currentPalette.background.paper, 0.6);
    const glassEdge = alpha(currentPalette.text.secondary, 0.25);

    return createTheme({
        spacing: 8,
        palette: {
            mode,
            primary: currentPalette.primary,
            secondary: currentPalette.secondary,
            background: {
                default: currentPalette.background.default,
                paper: currentPalette.background.paper,
            },
            text: currentPalette.text,
            info: currentPalette.info,
            success: currentPalette.success,
            warning: currentPalette.warning,
            error: currentPalette.error,
            divider: glassEdge,
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: currentPalette.background.default,
                        transition: 'background-color 0.3s ease',
                    },
                    input: {
                        '&:-webkit-autofill': {
                            boxShadow: `0 0 0 100px ${currentPalette.textField.autofillBackground} inset !important`,
                            transition: 'background-color 5000s ease-in-out 0s',
                            WebkitTextFillColor: currentPalette.text.primary,
                        },
                    },
                },
            },
            MuiSnackbarContent: {
                styleOverrides: {
                    root: {
                        backgroundColor: currentPalette.snackbar.background,
                        color: currentPalette.snackbar.textColor,
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        color: currentPalette.snackbar.textColor,
                        borderRadius: 8,
                    },
                    standardSuccess: {
                        backgroundColor: currentPalette.alert.success.main,
                        color: currentPalette.alert.success.contrastText,
                    },
                    standardError: {
                        backgroundColor: currentPalette.alert.error.main,
                        color: currentPalette.alert.error.contrastText,
                    },
                    standardWarning: {
                        backgroundColor: currentPalette.alert.warning.main,
                        color: currentPalette.alert.warning.contrastText,
                    },
                    standardInfo: {
                        backgroundColor: currentPalette.alert.info.main,
                        color: currentPalette.alert.info.contrastText,
                    },
                    outlinedSuccess: {
                        borderColor: currentPalette.alert.success.main,
                        color: currentPalette.alert.success.main,
                    },
                    outlinedError: {
                        borderColor: currentPalette.alert.error.main,
                        color: currentPalette.alert.error.main,
                    },
                    outlinedWarning: {
                        borderColor: currentPalette.alert.warning.main,
                        color: currentPalette.alert.warning.main,
                    },
                    outlinedInfo: {
                        borderColor: currentPalette.alert.info.main,
                        color: currentPalette.alert.info.main,
                    },
                },
            },
            MuiBackdrop: {
                styleOverrides: {
                    root: {
                        backgroundColor: `${currentPalette.background.default}80`,
                        backdropFilter: 'blur(10px)',
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: alpha(
                            currentPalette.background.paper,
                            0.7
                        ),
                        backdropFilter: 'blur(10px)',
                        borderBottom: `1px solid ${glassEdge}`,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: { textTransform: 'none' },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backgroundColor: glassPaper,
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${glassEdge}`,
                    },
                },
            },
        },
        typography: {
            fontFamily: 'Roboto, Arial, sans-serif',
            button: { fontWeight: 600 },
            h6: { fontWeight: 500 },
        },
        shape: {
            borderRadius: 8,
        },
    });
};

export default getTheme;
